import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { ShieldCheck } from "lucide-react";
import { getActiveAds, trackAdClick } from "../lib/api";
import { getVisitorId, isAnalyticsAllowed } from "../lib/consent";
import {
  AD_POOLS,
  AD_BREAKPOINT_BY_ROUTE,
  getAdClickUid,
  normalizeServerAd,
} from "../lib/ads";

/**
 * AdSlots renders the bottom ad area; previously placed above the EmergentAffiliate banner (now disabled, component retained).
 * - Desktop: three side-by-side tiles (at configurable breakpoint)
 * - Mobile: one tile
 * - Selection randomized on route change and on first mount (no interval timer)
 * - Dispatches window event 'rl-ads-status' with { emergentShowing: boolean }
 * - Absolutely no third-party scripts; just first-party links/images
 */
function seededShuffle(items, seed) {
  let x = (seed || Date.now()) >>> 0;
  const rnd = () => (x = (x * 1664525 + 1013904223) >>> 0);
  const result = [...items];
  for (let i = result.length - 1; i > 0; i--) {
    const j = rnd() % (i + 1);
    [result[i], result[j]] = [result[j], result[i]];
  }
  return result;
}

function pickGroup(pool, count, prevIds = []) {
  if (!pool.length) return [];
  const preferred = pool.filter((ad) => !prevIds.includes(ad.id));
  const source = preferred.length >= count ? preferred : pool;
  const picked = [];
  const usedTypes = new Set();

  for (const ad of source) {
    if (picked.length >= count) break;
    const type = String(ad.adType || "GENERAL");
    if (!usedTypes.has(type)) {
      picked.push(ad);
      usedTypes.add(type);
    }
  }
  for (const ad of source) {
    if (picked.length >= count) break;
    if (!picked.some((p) => p.id === ad.id)) {
      picked.push(ad);
    }
  }
  for (const ad of pool) {
    if (picked.length >= count) break;
    if (!picked.some((p) => p.id === ad.id)) {
      picked.push(ad);
    }
  }
  return picked;
}

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";

export default function AdSlots({ className = "", breakpoint }) {
  const location = useLocation();
  const [seed, setSeed] = useState(0);
  const [adPool, setAdPool] = useState(AD_POOLS);
  const [consentUid, setConsentUid] = useState(() => (isAnalyticsAllowed() ? getVisitorId() : null));
  const lastDesktopIdsRef = useRef([]);
  const lastMobileIdsRef = useRef([]);

  const bp = useMemo(() => {
    if (breakpoint) return breakpoint;
    const path = location.pathname || "/";
    const matchKey = Object.keys(AD_BREAKPOINT_BY_ROUTE).find((k) => k !== "default" && path.startsWith(k));
    return (matchKey && AD_BREAKPOINT_BY_ROUTE[matchKey]) || AD_BREAKPOINT_BY_ROUTE.default || "lg";
  }, [breakpoint, location.pathname]);

  useEffect(() => {
    // Re-roll on route change
    setSeed(Math.floor(Math.random() * 1e9));
  }, [location.pathname]);

  const isDesktopColsClass = bp === "md" ? "md:grid-cols-3" : "lg:grid-cols-3";
  const desktopSpacing = "md:gap-3 lg:gap-4";

  useEffect(() => {
    const refreshUid = () => {
      const uid = isAnalyticsAllowed() ? getVisitorId() : null;
      setConsentUid(uid);
    };
    refreshUid();
    window.addEventListener("rl_analytics_consent", refreshUid);
    return () => window.removeEventListener("rl_analytics_consent", refreshUid);
  }, []);

  useEffect(() => {
    let mounted = true;
    const loadAds = async () => {
      try {
        const data = await getActiveAds(consentUid || undefined);
        const items = Array.isArray(data?.items) ? data.items.map(normalizeServerAd) : [];
        const valid = items.filter((ad) => ad.id && ad.href && ad.imageUrl);
        if (mounted) {
          setAdPool(valid.length ? valid : AD_POOLS);
        }
      } catch (_) {
        if (mounted) setAdPool(AD_POOLS);
      }
    };
    loadAds();
    const id = window.setInterval(loadAds, 45000);
    return () => {
      mounted = false;
      window.clearInterval(id);
    };
  }, [consentUid]);

  const pickAds = useMemo(() => {
    const shuffled = seededShuffle(adPool, seed || Date.now());
    const desktop = pickGroup(shuffled, 3, lastDesktopIdsRef.current);
    const mobile = pickGroup(shuffled, 1, lastMobileIdsRef.current);
    while (desktop.length < 3) desktop.push({ id: `placeholder-${desktop.length}`, placeholder: true });
    return { desktop, mobile };
  }, [adPool, seed]);

  const [windowWidth, setWindowWidth] = useState(window.innerWidth);

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const adsToShow = useMemo(() => (windowWidth >= 768 ? pickAds.desktop : pickAds.mobile), [pickAds, windowWidth]);

  useEffect(() => {
    const ids = adsToShow.filter((ad) => !ad.placeholder).map((ad) => ad.id);
    if (windowWidth >= 768) {
      lastDesktopIdsRef.current = ids;
    } else {
      lastMobileIdsRef.current = ids;
    }
  }, [adsToShow, windowWidth]);

  const Placeholder = () => (
    <div className="block overflow-hidden rounded-2xl transition-all duration-300 min-h-[360px] md:min-h-[360px] bg-black/80" aria-hidden="true" data-testid="ad-slot-placeholder" />
  );

  const emergentShowing = useMemo(() => adsToShow.some((a) => !!a.emergent), [adsToShow]);

  useEffect(() => {
    try {
      window.dispatchEvent(new CustomEvent("rl-ads-status", { detail: { emergentShowing } }));
    } catch {}
  }, [emergentShowing]);

  const Tile = ({ ad }) => {
    const isExternal = /^https?:\/\//i.test(ad.href);
    const outboundHref = consentUid && BACKEND_URL
      ? `${BACKEND_URL}/api/ads/out/${encodeURIComponent(ad.id)}?uid=${encodeURIComponent(consentUid)}`
      : ad.href;
    const stubLabel = String(ad.stubLabel || ad.labelText || (ad.adType === "DONATION" ? "SUPPORT US" : "AFFILIATE AD")).trim();
    const interactionGuardRef = useRef(false);

    const onTrackClick = () => {
      try {
        const uid = getAdClickUid();
        if (!uid || !ad.id) return;

        if (consentUid && BACKEND_URL) {
          return;
        }

        trackAdClick(ad.id, uid).catch(() => {});
      } catch (_) {}
    };

    const onAdInteraction = () => {
      if (interactionGuardRef.current) return;
      interactionGuardRef.current = true;
      onTrackClick();
      window.setTimeout(() => {
        interactionGuardRef.current = false;
      }, 200);
    };

    const inner = (
      <div className="relative pt-3" data-testid={`ad-slot-wrap-${ad.id}`}>
        <div className="absolute left-3 top-0 z-20 -translate-y-1/2" data-testid={`ad-slot-tab-${ad.id}`}>
          <span className="inline-flex items-center gap-1.5 rounded-full border border-emerald-500/40 bg-black px-2.5 py-1 text-[10px] md:text-[11px] font-semibold tracking-wide text-emerald-100 uppercase shadow-[0_0_10px_rgba(16,185,129,0.2)]">
            <ShieldCheck className="h-3 w-3" /> {stubLabel}
          </span>
        </div>
        <div
          className="relative h-full rounded-2xl border border-white/10 bg-slate-950/65 overflow-hidden transition-all duration-300"
          data-testid={`ad-slot-tile-${ad.id}`}
        >
          {ad.imageUrl ? (
            ad.cropMode === 'cover-top' ? (
              <div className={`relative w-full ${ad.matchEmergentSize ? 'h-[360px] md:h-[360px]' : ''}`}>
                <img
                  src={ad.imageUrl}
                  alt={ad.alt || ad.title}
                  className="absolute inset-0 w-full h-full object-cover object-top select-none pointer-events-none scale-[1.02]"
                  style={{ transform: ad.cropZoom ? `scale(${ad.cropZoom / 100})` : undefined, transformOrigin: 'top center' }}
                  data-testid={`ad-slot-image-${ad.id}`}
                />
              </div>
            ) : (
              <div className={`relative w-full ${ad.matchEmergentSize ? 'h-[360px] md:h-[360px]' : ''}`}>
                <img src={ad.imageUrl} alt={ad.alt || ad.title} className="block w-full h-full object-cover object-center" data-testid={`ad-slot-image-${ad.id}`} />
              </div>
            )
          ) : (
            <div className={`px-5 py-6 text-center ${ad.matchEmergentSize ? 'h-[360px] md:h-[360px]' : ''}`}>
              <div className="text-base sm:text-lg font-semibold" data-testid={`ad-slot-title-${ad.id}`}>{ad.title}</div>
              {ad.subtitle ? (<div className="mt-1 text-sm opacity-90">{ad.subtitle}</div>) : null}
            </div>
          )}
        </div>
      </div>
    );

    return isExternal ? (
      <a
        href={outboundHref}
        target="_blank"
        rel="noopener noreferrer"
        className="group block transition-all duration-300 hover:brightness-[1.02]"
        onClick={onAdInteraction}
        data-testid={`ad-slot-link-${ad.id}`}
      >
        {inner}
      </a>
    ) : (
      <span
        onClick={() => {
          onAdInteraction();
          try { window.location.assign(outboundHref); } catch {}
        }}
        className="group block cursor-pointer transition-all duration-300 hover:brightness-[1.02]"
        data-testid={`ad-slot-link-${ad.id}`}
      >
        {inner}
      </span>
    );
  };

  // Render grid with per-tile wrappers
  return (
    <div className={`mx-auto max-w-7xl px-4 ${className}`} data-ad-container>
      <div className={`grid grid-cols-1 ${isDesktopColsClass} gap-6 ${desktopSpacing} mt-6 w-full`}>
        {adsToShow.map((ad, idx) => (
          ad.placeholder ? <Placeholder key={`placeholder-black-${idx}`} /> : <Tile key={ad.id} ad={ad} />
        ))}
      </div>
    </div>
  );
}